import { LocalDate } from '@js-joda/core';
import { i18n } from 'i18n';
import { DEFAULT_SHORT_DATE_NO_TIMEZONE_CONFIG, displayTemporal } from 'utils';
import PCSpool from './Spools/PCSpool';

// Which of a spool's dates the period is matched against.
export type SpoolDateField = 'inService' | 'installed' | 'removed';

// How the period was picked. Matching only reads from/to; this remembers which picker the user chose.
export type SpoolDatePeriod = 'year' | 'month' | 'day' | 'range';

export type UndatedSpools = 'show' | 'hide' | 'only';

export interface SpoolDateFilter {
  field: SpoolDateField;
  period: SpoolDatePeriod;
  /** Inclusive. Null leaves that end open. */
  from: LocalDate | null;
  to: LocalDate | null;
  undated: UndatedSpools;
}

export const NO_SPOOL_DATE_FILTER: SpoolDateFilter = {
  field: 'inService', period: 'year', from: null, to: null, undated: 'show',
};

// A spool with no dates is in service all along, as From Dates treats it, so "In service" shows it. One with no
// installed date was not installed in any period, so "Installed" and "Removed" hide it until asked otherwise.
export const defaultUndated = (field: SpoolDateField): UndatedSpools => (field === 'inService' ? 'show' : 'hide');

const hasPeriod = (filter: SpoolDateFilter): boolean => !!filter.from || !!filter.to;

export const isSpoolDateFilterActive = (filter: SpoolDateFilter): boolean => hasPeriod(filter) || filter.undated !== 'show';

const isUndated = (spool: PCSpool, field: SpoolDateField): boolean => {
  if (field === 'installed') return !spool.installedOn;
  if (field === 'removed') return !spool.removedOn;
  return !spool.installedOn && !spool.removedOn;
};

const within = (date: LocalDate, from: LocalDate | null, to: LocalDate | null): boolean => (
  (!from || !date.isBefore(from)) && (!to || !date.isAfter(to))
);

// In by the end of the period and not removed by its start. On a single day this is exactly the From Dates rule.
const inServiceDuring = (spool: PCSpool, from: LocalDate | null, to: LocalDate | null): boolean => (
  (!to || !spool.installedOn || !spool.installedOn.isAfter(to))
  && (!from || !spool.removedOn || spool.removedOn.isAfter(from))
);

const MATCHES: Record<SpoolDateField, (spool: PCSpool, from: LocalDate | null, to: LocalDate | null) => boolean> = {
  inService: inServiceDuring,
  installed: (spool, from, to) => !!spool.installedOn && within(spool.installedOn, from, to),
  removed: (spool, from, to) => !!spool.removedOn && within(spool.removedOn, from, to),
};

export const spoolMatchesDateFilter = (spool: PCSpool, filter: SpoolDateFilter): boolean => {
  if (isUndated(spool, filter.field)) return filter.undated !== 'hide';
  if (filter.undated === 'only') return false;
  return MATCHES[filter.field](spool, filter.from, filter.to);
};

// Year, month and day are the whole span containing the picked date.
export const periodAround = (period: Exclude<SpoolDatePeriod, 'range'>, date: LocalDate): { from: LocalDate; to: LocalDate } => {
  if (period === 'year') {
    const from = date.withDayOfYear(1);
    return { from, to: from.plusYears(1).minusDays(1) };
  }
  if (period === 'month') {
    const from = date.withDayOfMonth(1);
    return { from, to: from.plusMonths(1).minusDays(1) };
  }
  return { from: date, to: date };
};

export type SpanPeriod = 'year' | 'month';

const startOf = (period: SpanPeriod, date: LocalDate): LocalDate => periodAround(period, date).from;
const endOf = (period: SpanPeriod, date: LocalDate): LocalDate => periodAround(period, date).to;

// Switching picker keeps the span, widened to whole years or months, so 2018-2020 becomes January 2018 to December 2020.
export const withPeriod = (filter: SpoolDateFilter, period: SpoolDatePeriod): SpoolDateFilter => {
  if (period === 'range') return { ...filter, period };
  const start = filter.from ?? filter.to;
  if (!start) return { ...filter, period, from: null, to: null };
  if (period === 'day') return { ...filter, period, ...periodAround('day', start) };
  return { ...filter, period, from: startOf(period, start), to: endOf(period, filter.to ?? start) };
};

// A year or month period runs from the start of one to the end of another. Picking a start past the end, or an end
// before the start, collapses it to that one year or month.
export const withSpanStart = (filter: SpoolDateFilter, period: SpanPeriod, date: LocalDate | null): SpoolDateFilter => {
  if (!date) return { ...filter, from: null, to: null };
  const from = startOf(period, date);
  return { ...filter, from, to: filter.to && !filter.to.isBefore(from) ? filter.to : endOf(period, date) };
};

export const withSpanEnd = (filter: SpoolDateFilter, period: SpanPeriod, date: LocalDate | null): SpoolDateFilter => {
  if (!date) return { ...filter, to: filter.from ? endOf(period, filter.from) : null };
  const to = endOf(period, date);
  return { ...filter, from: filter.from && !filter.from.isAfter(to) ? filter.from : startOf(period, date), to };
};

// Moves the end a year or month at a time, so a span can grow cumulatively: 2018, 2018-2019, 2018-2020.
export const stepSpanEnd = (filter: SpoolDateFilter, period: SpanPeriod, steps: number): SpoolDateFilter => {
  if (!filter.from || !filter.to) return filter;
  const to = endOf(period, period === 'year' ? filter.to.plusYears(steps) : filter.to.withDayOfMonth(1).plusMonths(steps));
  return to.isBefore(filter.from) ? filter : { ...filter, to };
};

export const canShortenSpan = (filter: SpoolDateFilter, period: SpanPeriod): boolean => (
  !!filter.from && !!filter.to && endOf(period, filter.from).isBefore(filter.to)
);

// The no-date choice follows the field it describes, except "only", which says what the user is looking for.
export const withField = (filter: SpoolDateFilter, field: SpoolDateField): SpoolDateFilter => ({
  ...filter,
  field,
  undated: filter.undated === 'only' ? 'only' : defaultUndated(field),
});

export const spoolDateFieldLabel = (field: SpoolDateField): string => ({
  inService: i18n._('In service'),
  installed: i18n._('Installed'),
  removed: i18n._('Removed'),
})[field];

const shortDate = (date: LocalDate): string => displayTemporal(date, DEFAULT_SHORT_DATE_NO_TIMEZONE_CONFIG);

const monthYear = (date: LocalDate): string => displayTemporal(date, { month: 'short', year: 'numeric', timeZoneName: undefined });

const span = (first: string, last: string): string => (first === last ? first : `${first} – ${last}`);

const describePeriod = ({ period, from, to }: SpoolDateFilter): string => {
  if (from && to && period === 'year') return span(String(from.year()), String(to.year()));
  if (from && to && period === 'month') return span(monthYear(from), monthYear(to));
  if (from && to) return span(shortDate(from), shortDate(to));
  if (from) return i18n._('from {date}', { date: shortDate(from) });
  if (to) return i18n._('until {date}', { date: shortDate(to) });
  return i18n._('any date');
};

/** One line for the filter field, or '' when the filter is off. */
export const describeSpoolDateFilter = (filter: SpoolDateFilter): string => {
  if (!isSpoolDateFilterActive(filter)) return '';
  const field = spoolDateFieldLabel(filter.field);
  if (filter.undated === 'only') return i18n._('{field}: no date', { field });
  const summary = `${field}: ${describePeriod(filter)}`;
  if (filter.undated === defaultUndated(filter.field)) return summary;
  return filter.undated === 'show' ? `${summary} ${i18n._('(+ no date)')}` : `${summary} ${i18n._('(dated only)')}`;
};

// The From Dates tool works on one day, so it can start from the filter's day when the filter is on a single day.
export const spoolDateFilterDay = (filter: SpoolDateFilter): LocalDate | null => (
  filter.field === 'inService' && filter.undated !== 'only' && filter.from && filter.to && filter.from.equals(filter.to) ? filter.from : null
);
